import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Stack,
  Image,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { FaChevronLeft } from "react-icons/fa";
import {
  FaDollarSign,
  FaCalendarAlt,
  FaUsers,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { FiUser } from "react-icons/fi";
import { ChevronRightIcon } from "@chakra-ui/icons";
import type { CreativeBlock } from "./guidedFlow/types";
import type { BudgetData } from "./components/BudgetSelector";
import CreativeBlockReview from "./components/CreativeBlockReview";
import type { AudienceData } from "common/interfaces/KYDMarketing";
import { formatGuidedScheduleRangeDisplay } from "./utils/guidedScheduleEventWall";

export type ReviewEditorSection =
  | "general"
  | "budget"
  | "audience"
  | "creative";

function formatBudget(budget: BudgetData): string {
  const amount = budget?.amount?.amount;
  const type = budget?.type === "daily" ? "Daily" : "Lifetime";
  if (amount == null || amount <= 0) return "—";
  const currency = budget?.amount?.currency ?? "USD";
  const symbol = currency === "USD" ? "$" : currency + " ";
  return `${symbol}${Math.round(amount)} ${type} Budget`;
}

export type CreateCampaignReviewProps = {
  /** Event from draft (Algolia event object) */
  event: {
    name?: string;
    display_start_at?: string;
    venue?: { name?: string; address?: string };
    image?: string;
  } | null;
  adAccountName: string;
  budget: BudgetData;
  scheduleStart: string;
  scheduleEnd: string;
  /** IANA timezone for interpreting schedule wall times */
  scheduleEventTimezone: string;
  locationLabel?: string;
  targeting: {
    strategy: "template" | "custom";
    resolved_strategies:
      | { id?: string; name?: string; description?: string }[]
      | AudienceData[];
  };
  creatives: CreativeBlock[];
  onBackToEditor: (section?: ReviewEditorSection) => void;
  onLaunch: () => void;
  launching: boolean;
  launchLabel?: string;
  launchLoadingText?: string;
  sectionVisibility?: {
    budget: boolean;
    audience: boolean;
  };
  /** True while Facebook/Instagram identity data is still loading. */
  isIdentityDataLoading?: boolean;
  /** @deprecated Use isIdentityDataLoading. */
  isInstagramAccountsLoading?: boolean;
};

const ROW_ICON_SIZE = 4;
const EVENT_IMAGE_SIZE = "72px";

export default function CreateCampaignReview({
  event,
  adAccountName,
  budget,
  scheduleStart,
  scheduleEnd,
  scheduleEventTimezone,
  locationLabel,
  targeting,
  creatives,
  onBackToEditor,
  onLaunch,
  launching,
  launchLabel = "Launch Campaign",
  launchLoadingText = "Launching…",
  sectionVisibility = { budget: true, audience: true },
  isIdentityDataLoading = false,
  isInstagramAccountsLoading = false,
}: CreateCampaignReviewProps) {
  const eventName = event?.name ?? "Event";
  const eventDate = event?.display_start_at ?? "—";
  const venueLine =
    [event?.venue?.name, event?.venue?.address].filter(Boolean).join(" ") ||
    "—";
  const durationDisplay = formatGuidedScheduleRangeDisplay(
    scheduleStart,
    scheduleEnd,
    scheduleEventTimezone
  );
  const locationDisplay = locationLabel ?? venueLine;

  const isLaunchDisabled = isIdentityDataLoading || isInstagramAccountsLoading;

  return (
    <Stack
      direction={{ base: "column", md: "row" }}
      align="stretch"
      spacing={6}
      w="full"
      flex={1}
      minH={0}
    >
      {/* Left panel – Campaign Review */}
      <Box
        flex={1}
        bg="#FAFAFA"
        rounded="xl"
        maxW={{ base: "100%", md: "50%" }}
        minW={0}
        shadow="sm"
        borderWidth="1px"
        borderColor="gray.200"
        overflow="auto"
        display="flex"
        flexDirection="column"
      >
        <Box p={6} flex={1}>
          <Text fontSize="xl" fontWeight="bold" color="gray.800" mb={6}>
            Campaign Review
          </Text>

          {/* Campaign Event */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            rounded="xl"
            p={4}
          >
            <Box mb={6}>
              <Text fontSize="sm" color="gray.800" fontWeight="bold" mb={2}>
                Campaign Event
              </Text>
              <HStack align="center" justify="space-between" spacing={4}>
                <VStack align="stretch" spacing={0} flex={1} minW={0}>
                  <Text fontWeight="semibold" color="gray.800" noOfLines={1}>
                    {eventName}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {eventDate}
                  </Text>
                  <Text fontSize="sm" color="gray.600" noOfLines={1}>
                    {venueLine}
                  </Text>
                </VStack>
                <VStack flexShrink={0} align="flex-end" spacing={2}>
                  {event?.image ? (
                    <Image
                      src={event.image}
                      alt=""
                      w={EVENT_IMAGE_SIZE}
                      h={EVENT_IMAGE_SIZE}
                      objectFit="cover"
                      rounded="md"
                      bg="gray.100"
                    />
                  ) : (
                    <Box
                      w={EVENT_IMAGE_SIZE}
                      h={EVENT_IMAGE_SIZE}
                      rounded="md"
                      bg="gray.200"
                    />
                  )}
                </VStack>
              </HStack>
            </Box>

            {/* Campaign details list */}
            <VStack align="stretch" spacing={4} mb={2}>
              {[
                {
                  icon: FiUser,
                  label: "Ad Account",
                  value: adAccountName,
                  edit: false,
                },
                ...(sectionVisibility.budget
                  ? [
                      {
                        icon: FaDollarSign,
                        label: "Budget",
                        value: formatBudget(budget),
                        edit: true,
                        section: "budget" as const,
                      },
                      {
                        icon: FaCalendarAlt,
                        label: "Duration",
                        value: durationDisplay,
                        edit: true,
                        section: "budget" as const,
                      },
                    ]
                  : []),
                ...(sectionVisibility.audience
                  ? [
                      {
                        icon: FaUsers,
                        label: "Audience",
                        value:
                          targeting.strategy === "template"
                            ? "Smart Targeting"
                            : "Custom Targeting",
                        edit: true,
                        section: "audience" as const,
                      },
                    ]
                  : []),
                {
                  icon: FaMapMarkerAlt,
                  label: "Location",
                  value: locationDisplay,
                  edit: false,
                },
              ].map(({ icon: IconComponent, label, value, edit, section }) => (
                <HStack
                  key={label}
                  justify="space-between"
                  align="center"
                  w="full"
                  borderTop={"1px solid"}
                  borderColor="gray.200"
                  pt={2}
                >
                  <HStack spacing={3} minW={0} flex={1}>
                    <Flex
                      w={ROW_ICON_SIZE}
                      h={ROW_ICON_SIZE}
                      rounded="full"
                      bg="gray.100"
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <Icon
                        as={IconComponent}
                        boxSize={ROW_ICON_SIZE}
                        color="gray.500"
                      />
                    </Flex>
                    <VStack align="stretch" spacing={0} minW={0}>
                      <Text fontSize="xs" color="gray.500">
                        {label}
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" color="gray.800">
                        {value}
                      </Text>
                    </VStack>
                  </HStack>
                  {edit && (
                    <Button
                      variant="link"
                      fontSize="sm"
                      fontWeight="medium"
                      color="blue.600"
                      flexShrink={0}
                      onClick={() => onBackToEditor(section)}
                      cursor="pointer"
                      isDisabled={launching}
                      _hover={{ textDecoration: "underline" }}
                    >
                      Edit
                    </Button>
                  )}
                </HStack>
              ))}
            </VStack>
          </Box>

          {/* Audience Targeting Strategy */}
          <Box
            mt={4}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            rounded="xl"
            p={4}
          >
            <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={2}>
              Audience Targeting Strategy
            </Text>
            {targeting.strategy === "template" && (
              <Text fontSize="sm" color="gray.600" mb={3}>
                KYD automatically finds your best audience using event data and
                Meta AI across {targeting.resolved_strategies.length} ad sets:
              </Text>
            )}
            <VStack align="stretch" spacing={2} pl={0}>
              {targeting.resolved_strategies.map((audience, i) => (
                <Box
                  key={i}
                  borderTop={"1px solid"}
                  borderColor="gray.200"
                  py={2}
                >
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                    {audience.name}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {audience.description}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Box>
        </Box>

        {/* Footer buttons */}
        <HStack
          justify="space-between"
          w="full"
          p={6}
          pt={4}
          borderTopWidth="1px"
          borderColor="gray.100"
        >
          <Button
            variant="outline"
            size="md"
            bg="gray.100"
            borderColor="gray.300"
            color="gray.800"
            leftIcon={<FaChevronLeft style={{ marginRight: "4px" }} />}
            onClick={() => onBackToEditor("general")}
            _hover={{ bg: "gray.200" }}
            isDisabled={launching}
          >
            Back
          </Button>
          <Button
            colorScheme="green"
            size="md"
            rightIcon={<ChevronRightIcon />}
            onClick={onLaunch}
            isLoading={launching}
            isDisabled={isLaunchDisabled}
            loadingText={launchLoadingText}
          >
            {launchLabel}
          </Button>
        </HStack>
      </Box>

      {/* Right panel – Ad Media */}
      <Box
        flex={1}
        maxW={{ base: "100%", md: "50%" }}
        minW={0}
        bg="#FAFAFA"
        rounded="xl"
        shadow="sm"
        borderWidth="1px"
        borderColor="gray.200"
        overflow="auto"
        p={6}
      >
        <Text fontSize="xl" fontWeight="bold" color="gray.800" mb={6}>
          Ad Media
        </Text>

        <VStack align="stretch" spacing={8}>
          {creatives.map((creative, index) => (
            <CreativeBlockReview
              key={index}
              creative={creative}
              index={index}
              onEdit={() => onBackToEditor("creative")}
              launching={launching}
            />
          ))}
        </VStack>
      </Box>
    </Stack>
  );
}
